import { Component, Input, Output, EventEmitter, OnDestroy, OnChanges, SimpleChanges, ViewChildren, QueryList, ElementRef, ViewChild, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MultiselectComponent } from '../../../primeng/multiselect/multiselect.component';
import { TagInputComponent } from '../../../primeng/tag-input/tag-input.component';
import { CatalogueApiService, SearchCriteria } from '@angular-workspace/services';

export interface MetadataFormData {
  title: string;
  description: string;
  shortDescription: string;
  selectedIndustries: any[];
  selectedOrganisationTypes: any[];
  accessPolicy: string;
  tags: string[];
}

@Component({
  selector: 'app-metadata-information',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MultiselectComponent,
    TagInputComponent,
  ],
  templateUrl: './metadata-information.component.html',
  styleUrl: './metadata-information.component.scss',
})
export class MetadataInformationComponent implements OnDestroy, OnChanges {
  @Input() formData: MetadataFormData = {
    title: '',
    description: '',
    shortDescription: '',
    selectedIndustries: [],
    selectedOrganisationTypes: [],
    accessPolicy: '',
    tags: [],
  };

  @Input() validationErrors: { [key: string]: string } = {};

  @Output() formDataChange = new EventEmitter<MetadataFormData>();
  @Output() validationErrorsChange = new EventEmitter<{ [key: string]: string }>();
  @Output() titleAvailabilityChange = new EventEmitter<'checking' | 'available' | 'not-available' | null>();

  // Title availability check (search usecases by label - same as collection page)
  titleAvailabilityStatus: 'checking' | 'available' | 'not-available' | null = null;
  isCheckingTitle = false;
  private titleCheckTimeout: ReturnType<typeof setTimeout> | null = null;

  @ViewChildren(MultiselectComponent) multiselectComponents!: QueryList<MultiselectComponent>;
  @ViewChild('accessPolicyWrapper') accessPolicyWrapper!: ElementRef;

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (this.isAccessPolicyDropdownOpen && this.accessPolicyWrapper?.nativeElement && !this.accessPolicyWrapper.nativeElement.contains(target)) {
      this.closeAccessPolicyDropdown();
    }
  }

  constructor(private catalogueApiService: CatalogueApiService) {}

  ngOnDestroy(): void {
    if (this.titleCheckTimeout) {
      clearTimeout(this.titleCheckTimeout);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Only call search API when Title specifically changed (not on other field updates)
    const formDataChange = changes['formData'];
    if (!formDataChange || !this.formData?.title?.trim()) return;
    const prev = formDataChange.previousValue;
    const curr = formDataChange.currentValue;
    if (prev && curr && (prev.title || '') === (curr.title || '')) return;
    this.scheduleTitleAvailabilityCheck(this.formData.title);
  }

  // Dropdown state
  isAccessPolicyDropdownOpen: boolean = false;

  // Options for multiselects
  industryOptions: any[] = [
    { label: 'Agriculture', value: 'agriculture' },
    { label: 'Technology', value: 'technology' },
    { label: 'Healthcare', value: 'healthcare' },
    { label: 'Education', value: 'education' },
    { label: 'Finance', value: 'finance' },
  ];

  organisationTypeOptions: any[] = [
    { label: 'Private', value: 'Private' },
    { label: 'Public', value: 'Public' },
    { label: 'Academic Institution', value: 'Academic Institution' },
    { label: 'NGO/Non-profit', value: 'NGO/Non-profit' },
    { label: 'Other', value: 'Other' },
    { label: 'NA', value: 'NA' },
    { label: '', value: '' },
  ];

  accessPolicyOptions: any[] = [
    { label: 'Public', value: 'public', disabled: false },
    { label: 'Restricted', value: 'restricted', disabled: false },
    { label: 'Private', value: 'private', disabled: true },
    { label: 'PII', value: 'pii', disabled: true },
  ];

  hasError(field: string): boolean {
    return !!this.validationErrors[field];
  }

  getError(field: string): string {
    return this.validationErrors[field] || '';
  }

  toggleAccessPolicyDropdown(): void {
    const wasOpen = this.isAccessPolicyDropdownOpen;
    this.isAccessPolicyDropdownOpen = !this.isAccessPolicyDropdownOpen;
    if (this.isAccessPolicyDropdownOpen && !wasOpen) {
      this.closeAllMultiselects();
    }
  }

  closeAccessPolicyDropdown(): void {
    if (this.isAccessPolicyDropdownOpen) {
      this.isAccessPolicyDropdownOpen = false;
    }
  }

  closeAllMultiselects(): void {
    this.multiselectComponents?.forEach(ms => {
      if (ms.isOpen) ms.closeDropdown();
    });
  }

  onMultiselectTriggerMousedown(emittingMultiselect: MultiselectComponent): void {
    // Close other dropdowns on mousedown (before click) so the clicked trigger isn't blocked by overlapping panels.
    // Do NOT close the emitting multiselect - otherwise the subsequent click would re-open it.
    this.closeAccessPolicyDropdown();
    this.multiselectComponents?.forEach(ms => {
      if (ms !== emittingMultiselect && ms.isOpen) ms.closeDropdown();
    });
  }

  onMultiselectOpen(): void {
    this.closeAccessPolicyDropdown();
    const currentlyOpening = this.multiselectComponents?.find(ms => ms.isOpen);
    this.multiselectComponents?.forEach(ms => {
      if (ms !== currentlyOpening && ms.isOpen) ms.closeDropdown();
    });
  }

  isAccessPolicySelected(value: string): boolean {
    return this.formData.accessPolicy === value;
  }

  getSelectedAccessPolicyLabel(): string {
    const option = this.accessPolicyOptions.find(opt => opt.value === this.formData.accessPolicy);
    return option ? option.label : '';
  }

  isAccessPolicyDisabled(option: any): boolean {
    return !!option.disabled;
  }

  selectAccessPolicy(option: any): void {
    if (option.disabled) return;
    this.formData.accessPolicy = option.value;
    this.isAccessPolicyDropdownOpen = false;
    this.emitFormDataChange();
  }

  onTitleChange(value: string): void {
    this.formData.title = value;
    this.emitFormDataChange();
    this.scheduleTitleAvailabilityCheck(value);
  }

  private scheduleTitleAvailabilityCheck(title: string): void {
    if (this.titleCheckTimeout) {
      clearTimeout(this.titleCheckTimeout);
    }
    this.titleCheckTimeout = setTimeout(() => {
      this.titleCheckTimeout = null;
      this.checkTitleAvailability(title);
    }, 500);
  }

  async checkTitleAvailability(title: string): Promise<boolean> {
    if (!title || title.trim().length === 0) {
      this.titleAvailabilityStatus = null;
      this.titleAvailabilityChange.emit(null);
      return true;
    }

    this.titleAvailabilityStatus = 'checking';
    this.isCheckingTitle = true;
    this.titleAvailabilityChange.emit('checking');

    try {
      const searchCriteria: SearchCriteria[] = [
        { searchType: 'term', field: 'name', values: [title.trim()] }
      ];
      const response = await this.catalogueApiService.searchAllAssets(
        '',
        ['App'], // Usecases are adex:Apps
        [],
        1,
        10,
        'name:asc;itemCreatedAt:asc;',
        searchCriteria
      );

      let items: any[] = [];
      if (Array.isArray(response)) {
        items = response;
      } else if (response?.result && Array.isArray(response.result)) {
        items = response.result;
      } else if (response?.data && Array.isArray(response.data)) {
        items = response.data;
      } else if (response?.items && Array.isArray(response.items)) {
        items = response.items;
      }

      const titleLower = title.trim().toLowerCase();
      const hasMatch = items.some(item => {
        const itemName = (item.name || item.label || '').toLowerCase();
        return itemName === titleLower;
      });

      const isAvailable = !hasMatch;
      this.titleAvailabilityStatus = isAvailable ? 'available' : 'not-available';
      this.titleAvailabilityChange.emit(this.titleAvailabilityStatus);
      return isAvailable;
    } catch (error) {
      console.error('Error checking title availability:', error);
      this.titleAvailabilityStatus = null;
      this.titleAvailabilityChange.emit(null);
      return true;
    } finally {
      this.isCheckingTitle = false;
    }
  }

  onDescriptionChange(value: string): void {
    this.formData.description = value;
    this.emitFormDataChange();
  }

  onShortDescriptionChange(value: string): void {
    this.formData.shortDescription = value;
    this.emitFormDataChange();
  }

  onIndustriesChange(value: any[]): void {
    this.formData.selectedIndustries = value;
    this.emitFormDataChange();
  }

  onOrganisationTypesChange(value: any[]): void {
    this.formData.selectedOrganisationTypes = value;
    this.emitFormDataChange();
  }

  onTagsChange(value: string[]): void {
    this.formData.tags = value;
    this.emitFormDataChange();
  }

  private emitFormDataChange(): void {
    this.formDataChange.emit({ ...this.formData });
  }

  validate(): boolean {
    this.validationErrors = {};
    let isValid = true;

    // Validate Title
    if (!this.formData.title || this.formData.title.trim() === '') {
      this.validationErrors['title'] = 'Title is required';
      isValid = false;
    }

    // Validate Access Policy
    if (!this.formData.accessPolicy || this.formData.accessPolicy.trim() === '') {
      this.validationErrors['accessPolicy'] = 'Access Policy is required';
      isValid = false;
    }

    // Validate Short Description
    if (!this.formData.shortDescription || this.formData.shortDescription.trim() === '') {
      this.validationErrors['shortDescription'] = 'Short Description is required';
      isValid = false;
    }

    // Validate Long Description
    if (!this.formData.description || this.formData.description.trim() === '') {
      this.validationErrors['description'] = 'Long Description is required';
      isValid = false;
    }

    // Validate Industry
    if (!this.formData.selectedIndustries || this.formData.selectedIndustries.length === 0) {
      this.validationErrors['industry'] = 'Industry is required';
      isValid = false;
    }

    // Validate Organisation Type
    if (!this.formData.selectedOrganisationTypes || this.formData.selectedOrganisationTypes.length === 0) {
      this.validationErrors['organisationType'] = 'Organisation Type is required';
      isValid = false;
    }

    // Validate Tags
    if (!this.formData.tags || this.formData.tags.length === 0) {
      this.validationErrors['tags'] = 'Tags is required';
      isValid = false;
    }

    this.validationErrorsChange.emit({ ...this.validationErrors });
    return isValid;
  }
}
